import { useCallback, useEffect, useMemo } from 'react';

import {
  PRESCRIBER,
} from '../../constants/patientChart.constant';
import {
  NavBar, NavKey, enabledNavItems,
} from '../../components/patientChart';
import { PageHeading, useAvatarTitle } from '../../components/PageHeading';
import LinearProgress from '../../components/LinearProgress';
import { VisitsUI } from '../../components/scheduledVisits/VisitsUI';
import { FEATURE } from '../../utils/feature';
import useQueryParams from '../../hooks/useQueryParams';
import useNavItems from '../../hooks/useNavItems';
import useVisits from '../../hooks/useVisits';
import Logo from '../../assets/logo.png';
import '../patientChart/styles.css';

const hasFeature = FEATURE();

const SelectedNavItem = {
  key: NavKey.sv
};

const NavItems = enabledNavItems([
  NavKey.sv,
  NavKey.ao,
  NavKey.tasks,
  NavKey.pcm,
  NavKey.so,
]);

const BottomNavItems = enabledNavItems([
  NavKey.v1,
]);

export const ScheduledVisits = ({
  history,
  location,
  selectedPrescriberInfo: prescriber = PRESCRIBER(),
  scheduledVisits = {},
  asyncOrders = {},
  headerInfoAPI = {},
  tasksInfo = {},

  GetHeaderInfo: getPrescriber = async () => {},
  GetScheduleVisitsLV: getScheduledVisits = async () => {},
  GetPendingFilesLV: getPendingVisits = async () => {},
  GetTasks: getTasks = async () => {},
  SetFilterBy: setFilterBy = () => {},
  ClearScheduledVisitsApiStatus: clearApiStatus = () => {},
  LogOff: logOff = async () => ({}),

  ...rest
}) => {
  /**
   * @description useQueryParams is used to read the version from query parameter
   */
  const { json: { version: queryVersion = 1 } } = useQueryParams({ location });

  const { navItems, bottomNavItems } = useNavItems({
    scheduledVisits: scheduledVisits?.visits?.data || [],
    asyncOrders: asyncOrders?.visits?.data || [],
    tasks: tasksInfo?.data || [],
    items: NavItems,
    bottomItems: BottomNavItems,
    history,
  });

  useVisits({
    prescriber,
    scheduledVisits,
    asyncOrders,
    headerInfoAPI,
    tasksInfo,
    getTasks,
    getPrescriber,
    getPendingVisits,
    getScheduledVisits,
  });

  const { avatarTitle } = useAvatarTitle(prescriber);

  useEffect(() => {
    return () => {
      /**
       * @description clearing api status will reload the visits as soon as user navigates to the orders screen
       */
      // clearApiStatus();
      setFilterBy({ value: '', target: 'scheduledVisits' });
    };
  }, []);

  const handleRoute = useCallback((url = '') => {
    history.push(url);
  }, [history]);

  const PageHeadingComponent = new Map([
    [true, (
      <PageHeading
        logo={Logo}
        message={prescriber.Message}
        avatarTitle={avatarTitle}
        hasActions
        logOff={logOff}
        handler={{
          route: handleRoute,
        }}
      />
    )],
    [false, <></>]
  ]);

  const NavBarComponent = new Map([
    [true, <NavBar items={navItems} bottomItems={bottomNavItems} selectedItem={SelectedNavItem} />],
    [false, <></>]
  ]);

  const handleChange = (event, _, data = {}) => {
    if (!data?.UserId) {
      return false;
    }

    if (!data?.OrderId) {
      return false;
    }

    if (hasFeature.PATIENT_CHART_V3 && queryVersion === 3) {
      history.push(`/patientchart-v3/${data.OrderId}/${data.UserId}`);
      return true;
    }

    if (hasFeature.PATIENT_CHART_V2) {
      history.push(`/patientchart/${data.OrderId}/${data.UserId}`);
    }
    return true;
  };

  const isLoading = useMemo(() => {
    if (!scheduledVisits?.visits?.called) {
      return true;
    }

    return Boolean(scheduledVisits?.visits?.loading);
  }, [scheduledVisits]);

  return (
    <div className='patient-chart-container flex flex-column flex-fill'>
      <LinearProgress loading={isLoading} />

      {PageHeadingComponent.get(hasFeature.PAGE_HEADING)}

      <div className='patient-chart-content flex'>
        {NavBarComponent.get(hasFeature.NAV_BAR)}

        <VisitsUI
          {...rest}
          openView={0}
          scheduledVisits={scheduledVisits}
          setFilterBy={setFilterBy}
          handleRouteChange={handleChange}
          onRefresh={getScheduledVisits}
        />
      </div>
    </div>
  );
};

export default ScheduledVisits;
